import { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import tecnologiaData from '../../data/tecnologia.json';

// JSON-LD Structured Data for Tecnologia Page
const jsonLdData = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Desenvolvimento Sob Medida e Outsourcing de TI",
  "alternateName": "Fábrica de Software e Alocação Técnica",
  "url": "https://www.targetsoftware.com.br/servicos",
  "provider": {
    "@type": "Organization",
    "name": "Target Software",
    "url": "https://www.targetsoftware.com.br",
    "logo": "https://www.targetsoftware.com.br/assets/img/logo.png"
  },
  "description": "A Target Software oferece fábrica de software com foco em resultado, entregas ágeis, especialistas técnicos e de negócio, além de outsourcing e alocação técnica de profissionais 100% CLT com gestão, suporte corporativo e hunting especializado.",
  "serviceType": "Fábrica de Software e Outsourcing de TI",
  "areaServed": {
    "@type": "Country",
    "name": "Brasil"
  },
  "audience": {
    "@type": "BusinessAudience",
    "businessSize": "SMB/Enterprise",
    "industry": "Serviços Financeiros, Mercado de Capitais, Tecnologia, Indústria, Educação, Varejo"
  },
  "offers": {
    "@type": "Offer",
    "price": "Sob consulta",
    "priceCurrency": "BRL",
    "availability": "https://schema.org/InStock"
  },
  "brand": {
    "@type": "Brand",
    "name": "Target Software"
  },
  "hasPart": [
    {
      "@type": "Service",
      "name": "Fábrica de Software",
      "description": "Desenvolvimento sob medida com foco em resultado, tecnologia moderna, especialistas de negócio e entregas ágeis."
    },
    {
      "@type": "Service",
      "name": "Outsourcing de TI",
      "description": "Alocação técnica de profissionais com gestão das atividades, apoio corporativo e metodologia própria."
    },
    {
      "@type": "Service",
      "name": "Mergulho no Negócio",
      "description": "Time especialista atuando desde o início do projeto, garantindo entendimento profundo e entregas alinhadas."
    },
    {
      "@type": "Service",
      "name": "Metodologia Ágil",
      "description": "Processos estruturados que aceleram o time to market e asseguram controle e previsibilidade."
    },
    {
      "@type": "Service",
      "name": "Know-how Técnico e de Negócio",
      "description": "Profissionais atualizados atuando diretamente com times de negócio, sem linguagem técnica complexa."
    },
    {
      "@type": "Service",
      "name": "Hunting Técnico",
      "description": "Processo robusto para identificação e contratação dos melhores profissionais do mercado."
    },
    {
      "@type": "Service",
      "name": "Suporte Corporativo",
      "description": "RH e TI dedicados para garantir estabilidade, retenção de conhecimento e performance do time alocado."
    },
    {
      "@type": "Service",
      "name": "100% CLT",
      "description": "Profissionais contratados no modelo CLT, com benefícios completos e estabilidade para o projeto."
    }
  ],
  "keywords": [
    "fábrica de software",
    "desenvolvimento sob medida",
    "outsourcing de TI",
    "alocação técnica",
    "squads de desenvolvimento",
    "time dedicado",
    "software sob demanda",
    "profissionais CLT",
    "hunting técnico",
    "gestão de capacity"
  ],
  "image": "https://www.targetsoftware.com.br/assets/img/servicos-banner.png"
};

const TecnologiaPage = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);

  const { servicos, beneficiarios, faqs, tecnologias } = tecnologiaData;

  const totalSlides = Math.ceil(servicos.length - 1); // 2 cards por slide

  const nextService = () => {
    setCurrentServiceIndex((prev) => (prev + 1) % totalSlides);
  };

  const prevService = () => {
    setCurrentServiceIndex((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };

  // Add JSON-LD structured data to head
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLdData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header bannerHeightPercent={0.5} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-[#161e2d] py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-[#7d2128] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-[#7d2128] rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-blue-600 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium mb-6 text-white">
              <i className="ri-code-s-slash-line mr-2"></i>
              Tecnologia & Engenharia de Excelência
            </div>
            <h1 className="font-bold text-white mb-6 leading-tight">
              <span className="text-xl lg:text-2xl block mb-2">Transforme desafios tecnológicos em</span>
              <span className="text-4xl lg:text-6xl text-[#7d2128] block">Vantagem Competitiva</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Mais de 23 anos desenvolvendo soluções tecnológicas que elevam empresas a novos níveis de eficiência, inovação e escala.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-gray-400">
              <div className="flex items-center">
                <i className="ri-shield-check-line text-green-400 mr-2 text-xl"></i>
                <span>Equipes 100% CLT</span>
              </div>
              <div className="flex items-center">
                <i className="ri-robot-2-line text-green-400 mr-2 text-xl"></i>
                <span>Desenvolvimento com IA</span>
              </div>
              <div className="flex items-center">
                <i className="ri-award-line text-green-400 mr-2 text-xl"></i>
                <span>Resultados Comprovados</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introdução */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Seu Parceiro Estratégico em Tecnologia</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Se sua organização busca trazer valor aos dados, processos e relacionamentos, acelerar entregas de software, contar com equipes técnicas maduras ou estruturar operações com excelência, estamos prontos para ser seu parceiro estratégico.
            </p>
          </div>
        </div>
      </section>

      {/* Nossos Serviços - Carrossel */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nossos Serviços</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Soluções completas que cobrem todo o ciclo de tecnologia empresarial
            </p>
          </div>

          {/* Carrossel Desktop/Tablet */}
          <div className="hidden md:block relative">
            <div className="overflow-hidden">
              <div 
                className="flex transition-all duration-500 ease-out"
                style={{ transform: `translateX(-${currentServiceIndex * 50}%)` }}
              >
                {servicos.map((servico, index) => (
                  <div key={index} className="w-1/2 flex-shrink-0 px-4">
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden h-full">
                      {/* Header com gradiente azul */}
                      <div className="p-8 bg-gradient-to-br from-gray-900 via-gray-800 to-[#161e2d]">
                        <div className="flex items-center mb-4">
                          <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4 border border-white/20">
                            <i className={`${servico.icon} text-3xl text-white`}></i>
                          </div>
                          <h3 className="text-2xl font-bold text-white flex-1">{servico.title}</h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {servico.tags.map((tag, tagIdx) => (
                            <span
                              key={tagIdx}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/10 backdrop-blur-sm text-white border border-white/20"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Conteúdo */}
                      <div className="p-8">
                        <p className="text-lg text-gray-600 leading-relaxed mb-6">
                          {servico.description}
                        </p>

                        <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
                          Diferenciais
                        </h4>
                        <ul className="space-y-3">
                          {servico.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start">
                              <i className="ri-check-line text-xl mr-3 flex-shrink-0 mt-0.5 text-green-500"></i>
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Controles do Carrossel */}
            <button
              onClick={prevService}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
              aria-label="Anterior"
            >
              <i className="ri-arrow-left-s-line text-2xl text-gray-700"></i>
            </button>
            <button
              onClick={nextService}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
              aria-label="Próximo"
            >
              <i className="ri-arrow-right-s-line text-2xl text-gray-700"></i>
            </button>

            {/* Indicadores */}
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentServiceIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    currentServiceIndex === index ? 'w-8 bg-[#161e2d]' : 'w-2 bg-gray-300'
                  }`}
                  aria-label={`Ir para slide ${index + 1}`}
                ></button>
              ))}
            </div>
          </div>

          {/* Lista Mobile */}
          <div className="md:hidden space-y-6">
            {servicos.map((servico, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-6 bg-gradient-to-br from-gray-900 via-gray-800 to-[#161e2d]">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mr-3 border border-white/20">
                      <i className={`${servico.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-white flex-1">{servico.title}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {servico.tags.map((tag, tagIdx) => (
                      <span
                        key={tagIdx}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white/10 backdrop-blur-sm text-white border border-white/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 leading-relaxed mb-4 text-sm">
                    {servico.description}
                  </p>
                  <ul className="space-y-2">
                    {servico.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <i className="ri-check-line mr-2 flex-shrink-0 mt-0.5 text-green-500"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Nossas <span className="text-[#7d2128]">Stacks</span> de Tecnologia
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dominamos as principais tecnologias do mercado para entregar soluções robustas
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {tecnologias.map((tech, index) => (
              <div
                key={index}
                className="group bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-200 hover:border-[#7d2128]/30 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#7d2128]/10 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-[#7d2128]/20 transition-colors">
                    <i className={`${tech.icon} text-2xl text-[#7d2128]`}></i>
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-[#7d2128] transition-colors">
                    {tech.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quem se Beneficia */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-[#161e2d] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Diferenciais da Target Software</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Veja como podemos ajudar diretamente no seu negócio
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {beneficiarios.map((beneficiario, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all duration-300 hover:scale-105 cursor-pointer"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      <i className={`${beneficiario.icon} text-xl text-[#161e2d]`}></i>
                    </div>
                  </div>
                  <p className="text-gray-200 leading-relaxed">{beneficiario.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Perguntas Frequentes</h2>
            <p className="text-xl text-gray-600">Tire suas dúvidas sobre nossos serviços</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:border-[#7d2128]/20 transition-colors"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-2xl text-[#7d2128] flex-shrink-0`}></i>
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-[#161e2d] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Pronto para acelerar seus resultados?
          </h2>
          <p className="text-xl text-gray-100 mb-10 max-w-3xl mx-auto">
            Descubra como transformar tecnologia em vantagem competitiva.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contato"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#161e2d] font-semibold rounded-xl hover:bg-white transition-all duration-200 hover:scale-105"
            >
              <i className="ri-mail-send-line text-xl mr-3"></i>
              Fale com um Especialista
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TecnologiaPage;
